import { request } from './client';
import type { Problem } from './problem';
import type {
  AnswerType,
  CriticalScope,
  NotApplicableScope,
  QuestionOption,
  RequirementTrigger,
  ScoringModel,
} from './types';

export type FormStatus = 'Draft' | 'Published' | 'Archived';
export type VisitNotice = 'Announced' | 'Unannounced';

export interface FormTemplateSummary {
  id: string;
  code: string;
  name: string;
  version: number;
  status: FormStatus;
  description: string | null;
  visitTypeId: string | null;
  visitTypeName: string | null;

  checklistTitleId: string | null;
  checklistTitleName: string | null;

  /** Whether the visit ends with somebody at the store signing for it. */
  requiresSignature: boolean;
  /** The seats an action from this checklist travels, in order. Empty closes on the owner's word. */
  approvalSteps: string[];
  approvalStepTitles: string[];

  /** Which markets it is published for. Empty means every one of them. */
  countryIds: string[];
  countryNames: string[];

  /** Which kinds of place it applies at. Empty means every kind. */
  locationTypeIds: string[];
  locationTypeNames: string[];
  notice: VisitNotice;
  isScored: boolean;

  /** How this template's numbers are meant to be read. Frozen once it is published. */
  scoringModel: ScoringModel;

  /** What the question points add up to under points. Null under weights. */
  pointsTarget: number | null;

  /** What they actually add up to. Sent under both models. */
  totalQuestionWeight: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  questionCount: number;
  /** Computed from the status and the window — there is no separate active flag to disagree with it. */
  isAvailableToday: boolean;
}

export interface FormTemplateQuestion {
  id: string;
  code: string;
  text: string;
  weight: number;
  order: number;
  answerType: AnswerType;
  scaleMinimum: number;
  scaleMaximum: number;
  /** Copied from the library. Edited there, not here. */
  options: QuestionOption[];
  criticalScope: CriticalScope;
  commentRequirement: RequirementTrigger;
  photoRequirement: RequirementTrigger;
  allowsNotApplicable: boolean;
  notApplicableScope: NotApplicableScope;
  sourceQuestionId: string | null;
  /** Share of the whole audit, 0 to 1. The number the weight actually buys. */
  share: number;
}

export interface FormTemplateCategory {
  id: string;
  name: string;
  weight: number;
  order: number;
  sourceCategoryId: string | null;
  share: number;
  questions: FormTemplateQuestion[];
}

export interface FormTemplateDetail {
  summary: FormTemplateSummary;
  categories: FormTemplateCategory[];
  /** Everything that would stop it being published, all at once. */
  problems: Problem[];
  /**
   * The newer version of this code, when the one being looked at is not the newest.
   *
   * Null when this is the newest. A superseded template cannot be revised — the version a
   * revision would take is occupied — so the screen offers a way to the newer one instead
   * of a button that could only fail.
   */
  supersededBy?: { id: string; version: number } | null;
}

export interface SaveFormTemplate {
  code: string;
  name: string;
  description: string | null;
  visitTypeId: string | null;
  checklistTitleId: string | null;
  countryIds: string[];
  locationTypeIds: string[];
  requiresSignature: boolean;
  notice: VisitNotice;
  isScored: boolean;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

/**
 * The newest published version of each template code.
 *
 * <p>An older version stays Published on purpose — audits started on it finish on it — but
 * nobody should start new work on it. The server offers auditors only the newest; planning
 * must offer the same, or a plan opens v2 of a checklist that is on v3 (field test B-40).</p>
 */
export function newestPublished(list: FormTemplateSummary[]): FormTemplateSummary[] {
  const newest = new Map<string, FormTemplateSummary>();
  for (const f of list) {
    if (f.status !== 'Published') continue;
    const seen = newest.get(f.code);
    if (!seen || f.version > seen.version) newest.set(f.code, f);
  }
  return list.filter((f) => newest.get(f.code) === f);
}

/** The newest version number of each code, drafts included: what an older row was replaced by. */
export function latestVersions(list: FormTemplateSummary[]): Map<string, number> {
  const latest = new Map<string, number>();
  for (const f of list) {
    if (f.status === 'Draft') continue;
    latest.set(f.code, Math.max(latest.get(f.code) ?? 0, f.version));
  }
  return latest;
}

const base = '/api/v1/form-templates';

export const templates = {
  list: () => request<FormTemplateSummary[]>(base),

  /**
   * Which checklists an auditor standing in this location may fill in today.
   *
   * The point of the whole scope: without it the auditor is shown every published template
   * and picks, which is the one place in the flow where picking wrong is both easy and
   * expensive — the wrong checklist still produces a perfectly ordinary-looking score.
   */
  forLocation: (locationId: string, on?: string) =>
    request<FormTemplateSummary[]>(
      `${base}/for-location/${locationId}${on ? `?on=${on}` : ''}`,
    ),
  get: (id: string) => request<FormTemplateDetail>(`${base}/${id}`),

  create: (body: SaveFormTemplate) =>
    request<FormTemplateDetail>(base, { method: 'POST', body: JSON.stringify(body) }),

  update: (id: string, body: SaveFormTemplate) =>
    request<FormTemplateDetail>(`${base}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  remove: (id: string) => request<void>(`${base}/${id}`, { method: 'DELETE' }),

  addQuestions: (id: string, questionIds: string[], weight = 1) =>
    request<FormTemplateDetail>(`${base}/${id}/questions`, {
      method: 'POST',
      body: JSON.stringify({ questionIds, weight }),
    }),

  /**
   * Takes a category off the form, with whatever is still in it.
   *
   * Needed because removing the last question leaves the category behind, and an empty
   * category stops the template being published: without this the form could be edited
   * into a state it could not leave.
   */
  removeCategory: (id: string, categoryId: string) =>
    request<FormTemplateDetail>(`${base}/${id}/categories/${categoryId}`, { method: 'DELETE' }),

  removeQuestion: (id: string, questionId: string) =>
    request<FormTemplateDetail>(`${base}/${id}/questions/${questionId}`, { method: 'DELETE' }),

  setQuestionWeight: (id: string, questionId: string, weight: number) =>
    request<FormTemplateDetail>(`${base}/${id}/questions/${questionId}/weight`, {
      method: 'PUT',
      body: JSON.stringify({ weight }),
    }),

  moveQuestion: (id: string, questionId: string, position: number) =>
    request<FormTemplateDetail>(`${base}/${id}/questions/${questionId}/position`, {
      method: 'PUT',
      body: JSON.stringify({ position }),
    }),

  setCategoryWeight: (id: string, categoryId: string, weight: number) =>
    request<FormTemplateDetail>(`${base}/${id}/categories/${categoryId}/weight`, {
      method: 'PUT',
      body: JSON.stringify({ weight }),
    }),

  /** Only under relative weights. Under points a category already is the sum. */
  flattenCategoryWeights: (id: string) =>
    request<FormTemplateDetail>(`${base}/${id}/flatten-category-weights`, { method: 'POST' }),

  /** Switches a draft between models. Lossless both ways; the numbers typed stay. */
  setScoringModel: (id: string, body: { model: ScoringModel; pointsTarget: number | null }) =>
    request<FormTemplateDetail>(`${base}/${id}/scoring`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** Replaces the chain. A set, not an add: the order is the whole of the meaning. */
  setApprovalSteps: (id: string, positionIds: string[]) =>
    request<FormTemplateDetail>(`${base}/${id}/approval-steps`, {
      method: 'PUT',
      body: JSON.stringify({ positionIds }),
    }),

  publish: (id: string) => request<FormTemplateDetail>(`${base}/${id}/publish`, { method: 'POST' }),
  revise: (id: string) => request<FormTemplateDetail>(`${base}/${id}/revise`, { method: 'POST' }),
  archive: (id: string) => request<FormTemplateDetail>(`${base}/${id}/archive`, { method: 'POST' }),
};
